// src/core.ts
import { jitterMs, log, now, warn } from './utils';
import { startActivityTracking, stopActivityTracking, isActive, getLastActivity, getAndResetInteractionCount } from './activity';
import { becomeLeaderIfFree, handleStorageLeadership, relinquishLeadership, isLeader, fingerprintLite, getTabId, getPeerCount, startPresence } from './antifraud';
import { analyticsOn, consentFlags } from './consent';
import { initTransport, enqueue, flush, postIdentify, setTransportDisabled } from './transport';
import type { Config, SessionState, HeartbeatPayload, SessionStart, SessionEnd, UserProfile, RatePolicy, EventPayload } from './types';

let config: Required<Config>;
let session: SessionState | null = null;
let hbTimer: number | null = null;
let unStorage: (() => void) | undefined;
let fixedSessionId: string | null = null;
let hbSeq = 0;
let unPresence: (() => void) | undefined;
let identifyReady = false;
let identifyPromise: Promise<void> | null = null;
let waitingForIdentify = false;
let disabled = false;
let lastIdleState: boolean | null = null;

const DEFAULTS: Required<Config> = {
    projectKey: '',
    endpoint: '',
    consent: { default: 'pending', requireAnalytics: false },
    user: { profile: {} },
    billing: { heartbeatSec: 15, idleSec: 60, maxHoursPerDay: 6 },
    privacy: { piiFilter: true, dntRespect: true, countryHint: 'FR' },
    security: { hmacPublicKey: undefined as any, authSecret: undefined as any, tokenTtlSec: 600 },
    features: { bugReporter: true, replay: false, sentryBridge: false },
    sampling: { heartbeats: 1, events: 1, bugs: 1 },
    offline: { enabled: true, maxItems: 1000, maxAgeSec: 7 * 24 * 3600 },
    observability: { onTransportError: undefined as any, onTransportSuccess: undefined as any },
    screenshot: {
        enabled: false,
        selector: '[data-poulet-screenshot]',
        maxBytes: 1_000_000,
        mime: 'image/jpeg',
        quality: 0.85,
        minQuality: 0.5,
    },
    debug: false,
};

export function configure(cfg: Config) {
    config = { ...DEFAULTS, ...cfg, consent: { ...DEFAULTS.consent, ...cfg.consent } } as Required<Config>;
    if (!fixedSessionId) fixedSessionId = crypto.randomUUID();
    (window as any).__POULET_DEBUG__ = !!config.debug;
    initTransport({
        endpoint: config.endpoint,
        projectKey: config.projectKey,
        hmacPublicKey: config.security.hmacPublicKey,
        debug: config.debug,
        authSecret: config.security.authSecret,
        authTtlSec: config.security.tokenTtlSec,
        offline: config.offline,
        observability: config.observability,
        getAuthContext: () => ({
            userId: session?.userId ?? config.user?.id ?? config.user?.testerPseudoId ?? null,
            sessionId: session?.sessionId ?? fixedSessionId ?? null,
        }),
    });
}

export function sendIdentify(profile: UserProfile) {
    identifyReady = false;
    identifyPromise = (async () => {
        try {
            const sessionId = session?.sessionId ?? fixedSessionId ?? null;
            const userId = config.user?.id ?? config.user?.testerPseudoId ?? null;
            const result = await postIdentify(profile, sessionId, userId);
            if (result.ok && result.ratePolicy?.perSecondLimit) {
                applyRatePolicy(result.ratePolicy);
                if (session) scheduleHeartbeats();
            }
            if (!result.ok) {
                disabled = true;
                setTransportDisabled(true);
                warn('SDK disabled: /identify failed');
            }
        } finally {
            identifyReady = true;
        }
    })();
    return identifyPromise;
}

export function openSession() {
    if (session) return;
    if (disabled) return;
    if (config.consent.requireAnalytics && !analyticsOn()) return;
    if (!identifyReady) {
        if (!waitingForIdentify && identifyPromise) {
            waitingForIdentify = true;
            identifyPromise.finally(() => {
                waitingForIdentify = false;
                openSession();
            });
        }
        return;
    }
    const id = fixedSessionId ?? crypto.randomUUID();
    const fp = fingerprintLite();
    const userId = config.user?.id ?? config.user?.testerPseudoId;
    session = {
        sessionId: id,
        startedAt: now(),
        lastActivityAt: now(),
        active: true,
        leader: becomeLeaderIfFree(id),
        userId,
    };
    if (unStorage) unStorage();
    unStorage = handleStorageLeadership(id, (lead) => {
        if (session) session.leader = lead;
    });

    startActivityTracking();
    if (unPresence) unPresence();
    unPresence = startPresence();

    const startPayload: SessionStart = {
        type: 'session.start',
        sessionId: id,
        ts: now(),
        projectKey: config.projectKey,
        userId,
        tabId: getTabId(),
        tabCount: getPeerCount(),
        ua: fp.ua,
        tz: fp.tz,
        dpr: fp.dpr,
        consentFlags: { analytics: analyticsOn(), replay: false },
    };
    enqueue(startPayload);
    scheduleHeartbeats();
    log('session opened', id);
}

export function closeSession(reason: SessionEnd['reason']) {
    if (!session) return;
    if (disabled) return;
    stopHeartbeats();
    stopActivityTracking();
    relinquishLeadership(session.sessionId);
    const endPayload: SessionEnd = { type: 'session.end', sessionId: session.sessionId, ts: now(), reason };
    enqueue(endPayload);
    void flush();
    if (unStorage) unStorage();
    if (unPresence) unPresence();
    session = null;
    hbSeq = 0;
    log('session closed', reason);
}

function scheduleHeartbeats() {
    stopHeartbeats();
    const loop = () => {
        if (!session) return;
        if (!analyticsOn()) return; // consent required
        // @ts-ignore
        const idle = (now() - getLastActivity()) >= config.billing.idleSec * 1000;
        const vis = document.visibilityState === 'visible';
        const active = vis && !idle && session.leader;
        const fp = fingerprintLite();
        const lastActivityAgeMs = now() - getLastActivity();
        const interactions = getAndResetInteractionCount();

        if (lastIdleState === null) {
            lastIdleState = idle;
        } else if (idle !== lastIdleState) {
            lastIdleState = idle;
            const idleEvent: EventPayload = {
                type: 'event',
                sessionId: session.sessionId,
                ts: now(),
                userId: session.userId,
                name: idle ? 'idle.start' : 'idle.end',
                props: { lastActivityAgeMs },
                purpose: 'diagnostics',
            };
            enqueue(idleEvent);
        }

        // @ts-ignore
        if (Math.random() <= config.sampling.heartbeats && active) {
            const hb: HeartbeatPayload = {
                type: 'heartbeat',
                sessionId: session.sessionId,
                ts: now(),
                userId: session.userId,
                active: true,
                vis,
                idle: false,
                jitter: Math.random(),
                seq: ++hbSeq,
                lastActivityAgeMs,
                interactions,
                tabId: getTabId(),
                tabCount: getPeerCount(),
                ua: fp.ua,
                tz: fp.tz,
                dpr: fp.dpr,
                purpose: 'billing',
            };
            enqueue(hb);
        }

        // @ts-ignore
        const next = jitterMs(config.billing.heartbeatSec * 1000, 0.1);
        hbTimer = window.setTimeout(loop, next);
    };
    // @ts-ignore
    hbTimer = window.setTimeout(loop, jitterMs(config.billing.heartbeatSec * 1000, 0.1));
}

function stopHeartbeats() {
    if (hbTimer) {
        clearTimeout(hbTimer);
        hbTimer = null;
    }
}

export const getSessionIdSafe = () => session?.sessionId ?? null;

export const getUserIdSafe = () => session?.userId ?? config.user?.id ?? config.user?.testerPseudoId ?? null;

export const getScreenshotConfigSafe = () => config.screenshot;

function applyRatePolicy(policy: RatePolicy) {
    if (!policy.perSecondLimit || policy.perSecondLimit <= 0) return;
    config.billing.heartbeatSec = 1 / policy.perSecondLimit;
}
